import {Injectable} from '@angular/core';
import {ConnectionType, Network} from '@capacitor/network';
import {Subject} from 'rxjs';
import {BackendService, BackendServiceInstance} from './backend.service';
import {ReLoginCheck} from './helpers/ReLoginCheck';
import {BackendErrorEvent} from './DataModels/BackendModels';

// This whole service should be refactored:
//    - in case no internet connection is there, all other connections are offline for sure and no monitoring necessary until internet
//      comes back
//    - in case service is not there, service auth is not there as well
//    - in case service auth is there, service is there as well
@Injectable({
  providedIn: 'root'
})
export class ConnectivityService {

  public appIsOnline$: Subject<boolean> = new Subject<boolean>();
  public appIsOnline: boolean;
  public connectionType: ConnectionType;

  private _identityProviderOnline: boolean;
  public get identityProviderOnline(): boolean {
    return this._identityProviderOnline;
  }

  public set identityProviderOnline(value: boolean) {

    const switchedOffline = this.identityProviderOnline && !value;
    this._identityProviderOnline = value;
    this.identityProviderOnlineSubject.next(value);
    this.identityProviderOnlineFirstCheckFinished = true;
    if (switchedOffline) {
      this.monitorIdentityProvider();
    }
  }

  private _foodSearchOnline: boolean;
  public get foodSearchOnline(): boolean {
    return this._foodSearchOnline;
  }

  public set foodSearchOnline(value: boolean) {
    const switchedOffline = this.foodSearchOnline && !value;
    this._foodSearchOnline = value;
    this.foodSearchOnlineSubject.next(value);
    this.foodSearchOnlineFirstCheckFinished = true;
    if (switchedOffline) {
      this.foodSearchOnlineAuth = false;
      this.monitorFoodSearch();
    }
  }

  private _foodSearchOnlineAuth: boolean;
  public get foodSearchOnlineAuth(): boolean {
    return this._foodSearchOnlineAuth;
  }

  public set foodSearchOnlineAuth(value: boolean) {
    const switchedOffline = this.foodSearchOnlineAuth && !value;
    this._foodSearchOnlineAuth = value;
    this.foodSearchOnlineAuthSubject.next(value);
    if (value) {
      this.foodSearchOnline = true;
    }
    this.foodSearchOnlineAuthFirstCheckFinished = true;
    if (switchedOffline) {
      this.checkReLogin();
      this.monitorFoodSearchAuth();
    }
  }

  private _backendOnline: boolean;
  public get backendOnline(): boolean {
    return this._backendOnline;
  }

  public set backendOnline(value: boolean) {
    const switchedOffline = this.backendOnline && !value;
    this._backendOnline = value;
    this.backendOnlineSubject.next(value);
    this.backendOnlineFirstCheckFinished = true;
    if (switchedOffline) {
      this.backendOnlineAuth = false;
      this.monitorBackend();
    }
  }

  private _backendOnlineAuth: boolean;
  public get backendOnlineAuth(): boolean {
    return this._backendOnlineAuth;
  }

  public set backendOnlineAuth(value: boolean) {
    const switchedOffline = this.backendOnlineAuth && !value;
    this._backendOnlineAuth = value;
    this.backendOnlineAuthSubject.next(value);
    if (value) {
      this.backendOnline = true;
    }
    this.backendOnlineAuthFirstCheckFinished = true;
    if (switchedOffline) {
      this.checkReLogin();
      this.monitorBackendAuth();
    }
  }

  public get allServicesOnline(): boolean {
    return (this.appIsOnline && this.identityProviderOnline && this.foodSearchOnline && this.foodSearchOnlineAuth && this.backendOnline && this.backendOnlineAuth);
  }

  public identityProviderOnlineSubject: Subject<boolean> = new Subject<boolean>();
  public foodSearchOnlineSubject: Subject<boolean> = new Subject<boolean>();
  public foodSearchOnlineAuthSubject: Subject<boolean> = new Subject<boolean>();
  public backendOnlineSubject: Subject<boolean> = new Subject<boolean>();
  public backendOnlineAuthSubject: Subject<boolean> = new Subject<boolean>();

  private _identityProviderOnlineFirstCheckFinished = false;
  private get identityProviderOnlineFirstCheckFinished(): boolean {
    return this._identityProviderOnlineFirstCheckFinished;
  }

  private set identityProviderOnlineFirstCheckFinished(value: boolean) {
    const e = !this.initialServiceChecksFinished;
    this._identityProviderOnlineFirstCheckFinished = value;
    if (e && this.initialServiceChecksFinished) {
      this.initialServiceChecksFinishedSubject.next(true);
    }
  }

  private _foodSearchOnlineFirstCheckFinished = false;
  private get foodSearchOnlineFirstCheckFinished(): boolean {
    return this._foodSearchOnlineFirstCheckFinished;
  }

  private set foodSearchOnlineFirstCheckFinished(value: boolean) {
    const e = !this.initialServiceChecksFinished;
    this._foodSearchOnlineFirstCheckFinished = value;
    if (e && this.initialServiceChecksFinished) {
      this.initialServiceChecksFinishedSubject.next(true);
    }
  }

  private _foodSearchOnlineAuthFirstCheckFinished = false;
  private get foodSearchOnlineAuthFirstCheckFinished(): boolean {
    return this._foodSearchOnlineAuthFirstCheckFinished;
  }

  private set foodSearchOnlineAuthFirstCheckFinished(value: boolean) {
    const e = !this.initialServiceChecksFinished;
    this._foodSearchOnlineAuthFirstCheckFinished = value;
    if (e && this.initialServiceChecksFinished) {
      this.initialServiceChecksFinishedSubject.next(true);
    }
  }

  private _backendOnlineFirstCheckFinished = false;
  private get backendOnlineFirstCheckFinished(): boolean {
    return this._backendOnlineFirstCheckFinished;
  }

  private set backendOnlineFirstCheckFinished(value: boolean) {
    const e = !this.initialServiceChecksFinished;
    this._backendOnlineFirstCheckFinished = value;
    if (e && this.initialServiceChecksFinished) {
      this.initialServiceChecksFinishedSubject.next(true);
    }
  }

  private _backendOnlineAuthFirstCheckFinished = false;
  private get backendOnlineAuthFirstCheckFinished(): boolean {
    return this._backendOnlineAuthFirstCheckFinished;
  }

  private set backendOnlineAuthFirstCheckFinished(value: boolean) {
    const e = !this.initialServiceChecksFinished;
    this._backendOnlineAuthFirstCheckFinished = value;
    if (e && this.initialServiceChecksFinished) {
      this.initialServiceChecksFinishedSubject.next(true);
    }
  }


  public get initialServiceChecksFinished(): boolean {
    return (this.identityProviderOnlineFirstCheckFinished && this.foodSearchOnlineFirstCheckFinished && this.foodSearchOnlineAuthFirstCheckFinished && this.backendOnlineFirstCheckFinished && this.backendOnlineAuthFirstCheckFinished);
  }

  public initialServiceChecksFinishedSubject: Subject<boolean> = new Subject<boolean>();

  private monitorIntervall = 5000;

  constructor(private backend: BackendService) {

    this.initConnectivityMonitoring();
    this.subscribeConnectivityMonitoring();
    this.backend.errorSubject.subscribe(event => this.actOnBackendErrorEvent(event));
    this.onlineCheckIdentityProvider();
    this.onlineCheckFoodSearch();
    this.onlineCheckBackend();
  }

  private async initConnectivityMonitoring() {
    this.appIsOnline = (await Network.getStatus()).connected;
    Network.addListener('networkStatusChange', status => {
      console.log('Network status changed', status);
      this.connectionType = status.connectionType;
      if (status.connected !== this.appIsOnline) {
        this.appIsOnline$.next(status.connected);
      }
    });
  }

  private subscribeConnectivityMonitoring(): void {
    this.appIsOnline$.subscribe(online => {
      this.handleNewAppOnlineState(online);
    });
  }

  private handleNewAppOnlineState(online: boolean) {
    if (!this.appIsOnline && online) {
      setTimeout(async () => {
        const o = await this.triggerOnlineChecks();
        const a = await this.triggerAuthChecks();
        if (o && a) {
          console.log('Everything online again!');
        }
      }, 200);
    }
    this.appIsOnline = online;
    if (!online) {
      this.identityProviderOnline = false;
      this.foodSearchOnline = false; // Triggers food search auth offline
      this.backendOnline = false; // Triggers backend auth offline
    }
  }

  public async forceAppOnlineCheck(): Promise<void> {
    this.handleNewAppOnlineState((await Network.getStatus()).connected);
  }

  public async isIdentityProviderOnline(): Promise<boolean> {
    try {
      return (await this.backend.identityProviderOnline().toPromise().catch()).body;
    } catch {
      return false;
    }
  }

  public async isFoodSearchOnline(): Promise<boolean> {
    try {
      return (await this.backend.foodSearchOnline().toPromise().catch()).body;
    } catch {
      return false;
    }
  }

  public async isBackendOnline(): Promise<boolean> {
    try {
      return (await this.backend.backendOnline().toPromise().catch()).body;
    } catch {
      return false;
    }
  }

  public async isFoodSearchOnlineAuth(): Promise<boolean> {
    try {
      return (await (await this.backend.foodSearchOnlineAuth()).toPromise()).body;
    } catch {
      return false;
    }
  }

  public async isBackendOnlineAuth(): Promise<boolean> {
    try {
      return (await (await this.backend.backendOnlineAuth()).toPromise()).body;
    } catch {
      return false;
    }
  }

  public triggerOnlineCheck(service: BackendServiceInstance): void {
    switch (service) {
      case BackendServiceInstance.identityProvider:
        this.onlineCheckIdentityProvider();
        break;
      case BackendServiceInstance.foodSearch:
        this.onlineCheckFoodSearch();
        this.onlineAuthCheckFoodSearch();
        break;
      case BackendServiceInstance.backend:
        this.onlineCheckBackend();
        this.onlineAuthCheckBackend();
        break;
    }
  }

  public async triggerOnlineChecks(): Promise<boolean> {
    const i = await this.onlineCheckIdentityProvider();
    const fs = await this.onlineCheckFoodSearch();
    const b = await this.onlineCheckBackend();
    return i && fs && b;
  }

  public async triggerAuthChecks(): Promise<boolean> {
    const fs = await this.onlineAuthCheckFoodSearch();
    console.log('FoodSearchOnline:', fs);
    const b = await this.onlineAuthCheckBackend();
    console.log('BackendOnline:', b);
    // TO BE CLARIFIED!!! Why is first call for food search always failing?
    const fs2 = await this.onlineAuthCheckFoodSearch();
    console.log('FoodSearchOnline2:', fs2);
    return fs2 && b;
  }

  private checkReLogin(): void {
    console.log('Starting to check if re-login makes sense...');
    const check = new ReLoginCheck(this.backend);
    check.finishedSubject.subscribe(result => {
      if (result && check.reLoginRecommended) {
        console.log('Re-Login!');
        // this.auth.signIn();
      } else {
        console.log('No re-Login.');
      }
      check.finishedSubject.unsubscribe();
    });
  }

  private monitorIdentityProvider(): void {
    setTimeout(() => this.onlineCheckIdentityProvider(), this.monitorIntervall);
    this.onlineCheckIdentityProvider();
    const sub = this.identityProviderOnlineSubject.subscribe({
      next: result => {
        if (!result) {
          setTimeout(() => {
            this.onlineCheckIdentityProvider();
          }, this.monitorIntervall);
        } else {
          sub.unsubscribe();
        }
      },
      error: () => {
        setTimeout(() => {
          this.onlineCheckIdentityProvider();
        }, this.monitorIntervall);
      }
    });
  }

  private monitorFoodSearch(): void {
    setTimeout(() => this.onlineCheckFoodSearch(), this.monitorIntervall);
    const sub = this.foodSearchOnlineSubject.subscribe({
      next: result => {
        if (!result) {
          setTimeout(() => {
            this.onlineCheckFoodSearch();
          }, this.monitorIntervall);
        } else {
          sub.unsubscribe();
        }
      },
      error: () => {
        setTimeout(() => {
          this.onlineCheckFoodSearch();
        }, this.monitorIntervall);
      }
    });
  }

  private monitorFoodSearchAuth(): void {
    setTimeout(() => this.onlineAuthCheckFoodSearch(), this.monitorIntervall);
    const sub = this.foodSearchOnlineAuthSubject.subscribe({
      next: result => {
        if (!result) {
          setTimeout(() => {
            this.onlineAuthCheckFoodSearch();
          }, this.monitorIntervall);
        } else {
          sub.unsubscribe();
        }
      },
      error: () => {
        setTimeout(() => {
          this.onlineAuthCheckFoodSearch();
        }, this.monitorIntervall);
      }
    });
  }

  private monitorBackend(): void {
    setTimeout(() => this.onlineCheckBackend(), this.monitorIntervall);
    const sub = this.backendOnlineSubject.subscribe({
      next: result => {
        if (!result) {
          setTimeout(() => {
            this.onlineCheckBackend();
          }, this.monitorIntervall);
        } else {
          sub.unsubscribe();
        }
      },
      error: () => {
        setTimeout(() => {
          this.onlineCheckBackend();
        }, this.monitorIntervall);
      }
    });
  }

  private monitorBackendAuth(): void {
    setTimeout(() => this.onlineAuthCheckBackend(), this.monitorIntervall);
    const sub = this.backendOnlineAuthSubject.subscribe({
      next: result => {
        if (!result) {
          setTimeout(() => {
            this.onlineAuthCheckBackend();
          }, this.monitorIntervall);
        } else {
          sub.unsubscribe();
        }
      },
      error: () => {
        setTimeout(() => {
          this.onlineAuthCheckBackend();
        }, this.monitorIntervall);
      }
    });
  }

  private actOnBackendErrorEvent(event: BackendErrorEvent): void {
    // Don't do anything on 4XX errors
    if (event.event.status >= 400 && event.event.status < 500) {
      return;
    }
    // otherwise check availability of the service
    this.triggerOnlineCheck(event.service);
  }

  public async onlineCheckIdentityProvider(): Promise<boolean> {
    this.identityProviderOnline = await this.isIdentityProviderOnline();
    return this.identityProviderOnline;
  }

  public async onlineCheckFoodSearch(): Promise<boolean> {
    this.foodSearchOnline = await this.isFoodSearchOnline();
    return this.foodSearchOnline;
  }

  public async onlineAuthCheckFoodSearch(): Promise<boolean> {
    this.foodSearchOnlineAuth = await this.isFoodSearchOnlineAuth();
    return this.foodSearchOnlineAuth;
  }

  public async onlineCheckBackend(): Promise<boolean> {
    this.backendOnline = await this.isBackendOnline();
    return this.backendOnline;
  }

  async onlineAuthCheckBackend(): Promise<boolean> {
    this.backendOnlineAuth = await this.isBackendOnlineAuth();
    return this.backendOnlineAuth;
  }

}
